import { Checkbox } from '@/components/ui/checkbox'
import {
    createCurrencyFormatter,
    createNumberFormatter,
    type DataTableColumn,
} from '@/components/ui/data-table-customized'
import { PRODUCT_UNITS } from '@/modules/Contract/constants'
import type { InvoiceItem } from '@/modules/Invoice/types'
import { formatCurrency, formatDate, formatNumberUsage } from '@/utils/function'
import { generatePathDatacentral } from '@/utils/path'
import type { TFunction } from 'i18next'
import { Link } from 'react-router-dom'

export const getInvoiceItemColumns = (
    t: TFunction
): DataTableColumn<InvoiceItem>[] => [
    {
        key: 'service_id',
        title: t('service'),
        width: 350,
        cellClassName: 'truncate',
        pinned: 'left',
        render: (value, row) => {
            if (!row?.service_id) return <span>---</span>
            const isCDN = /cdn|content.?delivery/i.test(row?.service_name || '')
            return (
                <Link
                    to={generatePathDatacentral('/services/:id', {
                        id: value,
                    })}
                    className="text-primary hover:underline uppercase"
                >
                    {value} - {row?.service_name}
                    {isCDN && row?.invoice_item_name
                        ? ` - ${t(row.invoice_item_name)}`
                        : ''}
                </Link>
            )
        },
    },
    {
        key: 'invoice_item_name',
        title: t('invoice_item_name'),
        width: 500,
        cellClassName: 'truncate',
        render: (_, row) => {
            const text = row?.service_id
                ? [
                      t(
                          `invoices.descriptions.${row?.invoice_item_description}`
                      ),
                      (
                          row?.invoice_item_tags || row?.invoice_item_name
                      )?.toUpperCase(),
                      `(${formatDate(row?.invoice_item_apply_startdate)} - ${formatDate(row?.invoice_item_apply_enddate)})`,
                  ].join(' - ')
                : row?.invoice_item_name?.toUpperCase()
            return (
                <span className="uppercase truncate block" title={text}>
                    {text}
                </span>
            )
        },
    },
    {
        key: 'usage_quantity',
        title: t('usage_quantity'),
        width: 150,
        render: (value) => (
            <span className="w-full text-right block">
                {formatNumberUsage(value) || 0}
            </span>
        ),
    },
    {
        key: 'usage_unit',
        title: t('usage_unit'),
        width: 120,
        cellClassName: 'capitalize',
        render: (value) => {
            const unit = PRODUCT_UNITS(t)?.find((u) => u.value === value)
            return unit?.label || value || '-'
        },
    },
    {
        key: 'usage_price',
        title: t('usage_price'),
        width: 150,
        render: (value, row) => (
            <span className="w-full text-right block">
                {formatCurrency(value, row?.currency)}
            </span>
        ),
    },
    {
        key: 'invoice_item_discount',
        title: t('invoice_item_discount'),
        width: 150,
        formatter: createCurrencyFormatter({ currency: '' }),
    },
    {
        key: 'invoice_item_before_exchange_rate',
        title: t('invoice_item_before_exchange_rate'),
        width: 150,
        render: (value, row) => (
            <span className="w-full text-right block">
                {formatCurrency(value, row?.currency)}
            </span>
        ),
    },
    {
        key: 'exchange_rate',
        title: t('exchange_rate'),
        width: 130,
        cellClassName: 'text-right text-muted-foreground',
        formatter: createNumberFormatter({ decimals: 0 }),
    },
    {
        key: 'invoice_item_amount',
        title: t('invoice_item_amount'),
        width: 150,
        cellClassName: 'text-right',
        formatter: createCurrencyFormatter({ currency: '' }),
    },
    {
        key: 'invoice_item_taxable',
        title: t('invoice_item_taxable'),
        width: 80,
        render: (_, row) => (
            <div className="flex w-full items-center justify-center">
                <Checkbox
                    checked={Boolean(row?.invoice_item_taxable)}
                    onChange={() => {}}
                />
            </div>
        ),
    },
    {
        key: 'tax_percentage',
        title: 'VAT (%)',
        width: 100,
        cellClassName: 'text-right',
        valueGetter: (row) => row?.tax_percentage * 100,
        formatter: createNumberFormatter({ decimals: 2 }),
    },
    {
        key: 'tax_amount',
        title: t('tax_amount'),
        width: 150,
        cellClassName: 'text-right',
        valueGetter: (row) =>
            row?.invoice_item_taxable
                ? row?.invoice_item_amount * (row?.tax_percentage / 100)
                : 0,
        formatter: createCurrencyFormatter({ currency: '' }),
    },
]
